import React, { useContext } from "react";
import { Route as RouterRoute, Redirect } from "react-router-dom";

import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import SinAcceso from "../components/SinAcceso";
import { useAccesoVigente } from "../context/AccessControl/useAccesoVigente";

const Route = ({
	component: Component,
	isPrivate = false,
	requiredPermission = null,
	superOnly = false,
	...rest
}) => {
	const { isAuth, loading, user } = useContext(AuthContext);
	const acceso = useAccesoVigente();

	if (!isAuth && isPrivate) {
		return (
			<>
				{loading && <BackdropLoading />}
				<Redirect to={{ pathname: "/login", state: { from: rest.location } }} />
			</>
		);
	}

	if (isAuth && !isPrivate) {
		// Permitir acceso a rutas de autenticación incluso si está autenticado
		const authRoutes = ['/login', '/signup', '/privacy', '/terms', '/forgot-password', '/reset-password'];
		if (authRoutes.includes(rest.path)) {
			return (
				<>
					{loading && <BackdropLoading />}
					<RouterRoute {...rest} component={Component} />
				</>
			);
		}
		return (
			<>
				{loading && <BackdropLoading />}
				<Redirect to={{ pathname: "/tickets", state: { from: rest.location } }} />
			</>
		);
	}

	/**
	 * Pantallas de la PLATAFORMA (doc 0447, D6): datos de todas las empresas.
	 * WHY esperar al usuario en vez de dejar pasar: `isSuper` del backend responde
	 * 401, y un 401 el cliente lo trata como sesión caducada.
	 * Quien no es super va a Tickets, que es donde arranca toda cuenta: al cambiar de
	 * cuenta con la pestaña en /platform, la cuenta nueva aparecía ante un candado.
	 * Tickets no redirige nunca, así que no hay bucle posible.
	 * WHY dentro de RouterRoute: estas rutas no están en un <Switch> y TODAS se evalúan en
	 * cada página; decidir fuera mandaba a /tickets desde cualquier página (doc 0447 §10.15).
	 */
	if (isPrivate && superOnly) {
		return (
			<RouterRoute
				{...rest}
				render={(props) => {
					if (!user?.id) return <BackdropLoading />;
					if (!user.super) return <Redirect to="/tickets" />;
					return <Component {...props} />;
				}}
			/>
		);
	}

	/**
	 * La guarda de permiso, el último paso antes de pintar.
	 *
	 * WHY solo decide con el snapshot cargado: mientras `/me/permissions` viaja,
	 * negar el paso expulsaría de su propia página a quien sí tiene el permiso.
	 * Y si esa petición falla del todo, se deja pasar a propósito — quien
	 * protege de verdad es el backend, que exige el mismo permiso en cada
	 * endpoint. Esta guarda evita la pantalla vacía y el enlace engañoso; no es
	 * la cerradura.
	 */

	if (isPrivate && requiredPermission && acceso.loaded && !acceso.can(requiredPermission)) {
		return (
			<RouterRoute
				{...rest}
				render={() => <SinAcceso permiso={requiredPermission} />}
			/>
		);
	}

	return (
		<>
			{loading && <BackdropLoading />}
			<RouterRoute {...rest} component={Component} />
		</>
	);
};

export default Route;
